import { useEffect, useRef, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useAppDispatch } from "../../store/hooks";
import { adminAuthSliceActions } from "./AdminAuthSlice";

const ACTIVITY_EVENTS = [
  "mousemove",
  "keydown",
  "mousedown",
  "touchstart",
] as const;

const useIdleTimeout = (timeout: number = 3600000, onIdle?: () => void) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useAppDispatch();
  const { instance } = useMsal();

  const resetTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => handleIdle(), timeout);
  }, [timeout]);

  const handleIdle = useCallback(() => {
    if (onIdle) {
      onIdle(); // Execute custom idle logic if provided
    } else {
      dispatch(adminAuthSliceActions.clearUser());
      instance.logoutRedirect().catch((error) => {
        console.error("Error during logout redirect:", error);
      });
    }
  }, [dispatch, instance, onIdle]);

  useEffect(() => {
    const handleActivity = () => resetTimeout();

    // Add event listeners
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity)
    );

    // Initialize timeout on mount
    resetTimeout();

    // Cleanup on unmount
    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      );
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [resetTimeout]);

  return resetTimeout;
};

export default useIdleTimeout;
